import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { RiskFlag } from "@smb/shared";
import { severityColor } from "../lib/data";

export function Card({
  title,
  subtitle,
  children,
  className = "",
}: {
  title?: string;
  subtitle?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-lg border ${className}`}
      style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}
    >
      {title && (
        <header className="px-4 pt-4 pb-2">
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && (
            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
              {subtitle}
            </p>
          )}
        </header>
      )}
      <div className="px-4 pb-4">{children}</div>
    </section>
  );
}

export function Loading({ what }: { what: string }) {
  return (
    <p className="text-sm py-12 text-center" style={{ color: "var(--text-muted)" }}>
      Loading {what}…
    </p>
  );
}

export function ErrorNote({ error, what }: { error: string; what: string }) {
  return (
    <Card>
      <p className="text-sm font-medium" style={{ color: "var(--status-critical)" }}>
        Could not load {what}
      </p>
      <p className="text-xs mt-1 font-mono" style={{ color: "var(--text-secondary)" }}>
        {error}
      </p>
      <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
        The engine writes these files. Run <code>npm run engine -- score</code> and rebuild, or check
        that the nightly job succeeded.
      </p>
    </Card>
  );
}

/**
 * The composite score.
 *
 * Rendered as a plain number on a neutral chip, deliberately without a
 * red-to-green ramp. P&L elsewhere uses conventional green/red — an outcome is
 * an outcome — but a score is a ranking, not an outcome, and colouring it by
 * value would read as "green means buy", which is precisely the inference the
 * backtest says is unsupported.
 */
export function ScoreChip({ score }: { score: number }) {
  return (
    <span
      className="tnum inline-flex items-center justify-center rounded border px-2 py-0.5 text-sm font-semibold tabular-nums"
      style={{
        background: "var(--surface-2)",
        borderColor: "var(--border-strong)",
        color: "var(--text-primary)",
      }}
      title="Composite factor score, 0-100. 50 is sector-typical. Not a prediction."
    >
      {score}
    </span>
  );
}

export function RiskFlagList({ flags, compact }: { flags: readonly RiskFlag[]; compact?: boolean }) {
  if (flags.length === 0) {
    return (
      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
        No risk flags raised. That is not the same as low risk — it means none of the specific
        conditions this model checks were met.
      </p>
    );
  }

  if (compact) {
    return (
      <span className="flex flex-wrap gap-1">
        {flags.map((f) => (
          <span
            key={f.id}
            title={f.message}
            className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border"
            style={{ color: severityColor(f.severity), borderColor: "var(--border-strong)" }}
          >
            {f.id.replace(/_/g, " ").toLowerCase()}
          </span>
        ))}
      </span>
    );
  }

  return (
    <ul className="space-y-2">
      {flags.map((f) => (
        <li key={f.id} className="flex gap-2 text-sm">
          <span
            aria-hidden
            className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
            style={{ background: severityColor(f.severity) }}
          />
          <span>
            <span className="font-medium" style={{ color: severityColor(f.severity) }}>
              {f.severity}
            </span>
            <span style={{ color: "var(--text-secondary)" }}> — {f.message}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function SymbolLink({ symbol, name }: { symbol: string; name?: string }) {
  return (
    <Link
      to={`/ticker/${symbol}`}
      className="hover:underline underline-offset-2"
      style={{ color: "var(--text-primary)" }}
    >
      <span className="font-medium">{symbol}</span>
      {name && (
        <span className="ml-2 text-xs" style={{ color: "var(--text-muted)" }}>
          {name}
        </span>
      )}
    </Link>
  );
}

/** A short caveat rendered inline, so a number never appears without its limits. */
export function Caveat({ children }: { children: ReactNode }) {
  return (
    <p
      className="text-xs mt-2 pl-3 border-l-2"
      style={{ color: "var(--text-muted)", borderColor: "var(--border-strong)" }}
    >
      {children}
    </p>
  );
}
